import React, { useEffect, useState } from "react";
import { IconButton } from "@mui/material";
import { IoClose } from "react-icons/io5";
import { FiCheck } from "react-icons/fi";
import RsModal from "../../../../Components/UI/ma-modal";
import MaButton from "../../../../Components/UI/ma-button";
import boxImg from "../../../../assets/images/box.png"
import { IoIosCheckmarkCircleOutline } from "react-icons/io";

const QUICK_ADD = [5, 10, 25, 50, 100];

/**
 * Stock Up — adjust inventory with stepper + quick-add chips (seller dashboard).
 */
export default function StockUpModal({ open, onClose, product, onConfirm }) {
  const [unitsToAdd, setUnitsToAdd] = useState(10);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setUnitsToAdd(10);
      setError("");
      setSubmitting(false);
      setSuccessOpen(false);
    }
  }, [open, product?.id]);

  useEffect(() => {
    if (!successOpen) return undefined;
    const timeout = window.setTimeout(() => {
      setSuccessOpen(false);
      onClose?.();
    }, 1300);
    return () => window.clearTimeout(timeout);
  }, [successOpen, onClose]);

  if (!product) return null;

  const currentStock = Number(product.stock) || 0;

  const bump = (delta) => {
    setUnitsToAdd((u) => Math.max(0, u + delta));
  };

  const handleConfirm = async () => {
    if (unitsToAdd < 1 || submitting) return;
    setError("");
    setSubmitting(true);
    try {
      await onConfirm?.({
        productId: product.id,
        quantity: unitsToAdd,
        name: product.name,
      });
      setSuccessOpen(true);
    } catch (e) {
      const msg =
        e?.graphQLErrors?.[0]?.message ||
        e?.networkError?.message ||
        e?.message ||
        "Could not update stock. Try again.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <RsModal
      isOpen={open}
      onClose={() => {
        if (!submitting && !successOpen) onClose?.();
      }}
    >
      <div className="relative">
      {successOpen ? (
        <div className="fixed left-1/2 top-6 z-[1500] flex w-[min(504px,calc(100vw-32px))] -translate-x-1/2 items-center justify-between gap-4 rounded-[16px] bg-[#282828] px-7 py-2 shadow-[0_18px_45px_rgba(0,0,0,0.24)] sm:px-8">
          <div className="flex items-center gap-5">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full border-2 border-[#00A76F] text-[#00A76F]">
              <IoIosCheckmarkCircleOutline className="text-[22px]" />
            </span>
            <span className="text-[13px] font-medium text-white sm:text-[15px]">
              Product stock up successful
            </span>
          </div>
          <button
            type="button"
            onClick={() => {
              setSuccessOpen(false);
              onClose?.();
            }}
            className="grid h-10 w-10 shrink-0 cursor-pointer place-items-center rounded-full border-0 bg-transparent text-[#8B93A1] transition-colors hover:text-white"
            aria-label="Dismiss success message"
          >
            <IoClose className="text-[42px]" />
          </button>
        </div>
      ) : null}

      <div className="w-[min(440px,calc(100vw-32px))] overflow-hidden rounded-[22px] bg-white shadow-[0_24px_64px_rgba(15,23,42,0.14)]">
        {/* Header */}
        <header className="flex items-center justify-between px-7 pb-2 pt-7">
          <h2 className="my-0 text-[18px] font-semibold tracking-tight text-[#141414]">
            Stock Up
          </h2>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close"
            sx={{
              bgcolor: "#F7F7F5",
              color: "#DC2626",
              borderRadius: "10px",
              "&:hover": { bgcolor: "#FECACA" },
            }}
          >
            <IoClose className="text-xl" />
          </IconButton>
        </header>

        <div className="px-7 pb-7 pt-2">
          {/* Current stock */}
          <div className="mb-8 border border-solid border-[#E5E4E2] flex items-center justify-between gap-4 rounded-[10px] bg-[#F7F7F5] px-5 py-5">
            <div>
              <p className="my-0 text-[11px] font-semibold uppercase tracking-wider text-[#9B9895]">
                Current stock
              </p>
              <p className="my-0 mt-2 text-[34px] font-black leading-none tracking-tight text-[#18181B]">
                {currentStock}
              </p>
            </div>
            <div
              className="flex h-[52px] w-[72px] "
              aria-hidden
            >
            <img src={boxImg} />
            </div>
          </div>

          {/* Add units */}
          <div className="mb-5">
            <p className="my-0 mb-3 text-[14px] font-semibold text-[#27272A]">
              Add Units
            </p>

            <div className="flex overflow-hidden rounded-xl border border-solid border-[#EAEAEA] bg-white">
              <div className="flex flex-1 items-center px-4 py-3">
                <span className="text-[13px] text-[#6B6966]">Units to add</span>
              </div>
              <div className="flex items-center gap-x-3 border-l border-[#E4E4E7] pr-1">
                <IconButton
                  size="small"
                  onClick={() => bump(-1)}
                  aria-label="Decrease units"
                  sx={{
                    borderRadius: "10px",
                    color: "#52525B",
                    border:'1px solid #D0CECC',
                    width:'30px',
                    height:'30px'
                  }}
                >
                  <span className="text-lg font-medium leading-none">−</span>
                </IconButton>
                <span className="min-w-[28px] text-center text-[15px] font-bold tabular-nums text-[#18181B]">
                  {unitsToAdd}
                </span>
                <IconButton
                  size="small"
                  onClick={() => bump(1)}
                  aria-label="Increase units"
                  sx={{
                  borderRadius: "10px",
                    color: "#52525B",
                    border:'1px solid #D0CECC',
                    width:'30px',
                    height:'30px'
                  }}
                >
                  <span className="text-lg font-medium leading-none">+</span>
                </IconButton>
              </div>
            </div>
          </div>

          {/* Quick add */}
          <div className="mb-6 flex flex-wrap gap-2">
            {QUICK_ADD.map((n) => (
              <div
                key={n}
                type="button"
                onClick={() => bump(n)}
                className="rounded-[10px] shadow-none border border-solid cursor-pointer border-[#E4E4E7] bg-white px-3.5 py-2 text-[13px] font-semibold text-[#3F3F46] transition-colors hover:bg-[#FAFAFA] active:bg-[#F4F4F5]"
              >
                +{n}
              </div>
            ))}
          </div>

          {error ? (
            <p className="mb-4 my-0 text-[13px] text-[#DC2626] leading-snug">
              {error}
            </p>
          ) : null}

          {/* Footer */}
          <div className="flex justify-end gap-3">
            <MaButton
              title="Cancel"
              onClick={onClose}
              disabled={submitting || successOpen}
              sx={{
                bgcolor: "#FFFFFF",
                color: "#DC2626",
                border: "1px solid #E4E4E7",
                boxShadow: "none",
                minHeight: "46px",
                minWidth: "110px",
                borderRadius: "12px",
                fontWeight: 600,
                "&:hover": {
                  bgcolor: "#FAFAFA",
                  borderColor: "#D4D4D8",
                },
              }}
            />
            <MaButton
              variant="primary"
              title="Confirm"
              loading={submitting}
              disabled={submitting || successOpen || unitsToAdd < 1}
              onClick={handleConfirm}
              sx={{
                minHeight: "46px",
                minWidth: "120px",
                borderRadius: "12px",
                fontWeight: 600,
                bgcolor: "#1E1423",
                boxShadow: "0 8px 20px rgba(30, 20, 35, 0.25)",
                "&:hover": { bgcolor: "#2E1834" },
                "&:disabled": { opacity: 0.6 },
              }}
            />
          </div>
        </div>
      </div>
      </div>
    </RsModal>
  );
}
